/**
 * Per-player roster figures derived from the game state: territories,
 * troops, elimination, region bonuses, next reinforcement and card count.
 * Pure so it can be tested directly; `usePlayerInfo` (PlayerRosterPanel)
 * memoises it for the roster strip, the drawer and the top-bar dots.
 */
import { fogOfWarOf } from '@warcarp/api-types'
import type { GamePlayerResponse } from '@warcarp/api-types'
import { reinforcementCount } from './gameState'
import type { GameState } from './gameState'
import { resignedSlotIds } from './playerStatus'
import { fogViewerSide } from './teamPlay'

export interface PlayerInfo {
  player: GamePlayerResponse
  owned: number
  /** Sum of armies across every territory the player currently owns. */
  totalTroops: number
  eliminated: boolean
  /**
   * True iff this player left by RESIGNING (a `resign` event) rather than
   * being eliminated in combat. Only meaningful when `eliminated` is true —
   * a resigner's territories are neutralized so they always read eliminated.
   */
  resigned: boolean
  /** True iff this player's slot id matches the engine's current turn. */
  isTurn: boolean
  regionBonuses: Array<{ id: string; name: string; bonus: number }>
  nextReinforce: number
  cardCount: number
  /**
   * Fog of war only: set for players outside the viewer's side, whose totals
   * the viewer can't know. `owned`/`totalTroops` then cover only what the
   * viewer sees (light fog: `owned` and `regionBonuses` are exact, since
   * ownership is public), and `nextReinforce`/`cardCount` are 0 and must not
   * be shown. See docs/fog-of-war/README.md.
   */
  fog?: { seenTroops: number; seenTerritories: number; ownershipPublic: boolean }
}

/**
 * Pre-compute each player's territory count, eliminated state, region
 * bonuses, and next-turn reinforcement count once and share across the
 * strip + expanded panel so the math doesn't run twice per render.
 *
 * isTurn is derived from the engine's turn pointer (state.players[turnIndex])
 * compared against the player's slot id — NOT from the array index of
 * game.players, which can drift out of alignment with the engine's
 * own ordering (e.g. after elimination, or if the API ever sorts the
 * roster differently than the engine's player list).
 */
export function computePlayerInfo(state: GameState, players: GamePlayerResponse[]): PlayerInfo[] {
  const currentTurnPlayerId = state.players[state.turnIndex] ?? null
  // Fog of war: the server keeps only the viewer's side's hands, so the side
  // is the hands present, widened to the viewer's teammates in a team game (a
  // team shares one view). Everyone else's totals are unknown.
  const fog = fogOfWarOf(state.config)
  const side = fogViewerSide(state, fog !== 'none')
  // Slots that left via resignation (first-class `state.resigned`) vs combat
  // elimination — lets the panel label a resigner "resigned".
  const resignedIds = resignedSlotIds(state)
  // Turn order is fixed when the game starts and lives in state.players
  // (the play order). Position each roster entry by its index there so the
  // bar reads first-to-act → last, left → right — independent of the API's
  // slot/join ordering of the `players` array.
  const turnPos = (slot: number) => {
    const i = state.players.indexOf(String(slot))
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  return players.map((p) => {
    const playerId = String(p.slot)
    const myTerritories = state.map.territories.filter((t) => t.ownerId === playerId)
    const owned = myTerritories.length
    // Fogged territories carry no troop count (light fog keeps their owner).
    const seen = myTerritories.filter((t) => !t.fogged)
    const totalTroops = seen.reduce((sum, t) => sum + t.armies, 0)
    const hidden = side !== null && !side.has(playerId)
    // In fog a player can own nothing the viewer sees and still be alive, so
    // only the official list counts.
    const eliminated = side !== null
      ? state.eliminated.includes(playerId)
      : state.eliminated.includes(playerId) || owned === 0
    const regionBonuses = (hidden && fog === 'heavy' ? [] : state.map.regions)
      .filter((r) =>
        r.territoryIds.every((tid) => {
          const territory = state.map.territories.find((t) => t.id === tid)
          return territory !== undefined && territory.ownerId === playerId
        }),
      )
      .map((r) => ({ id: r.id, name: r.name, bonus: r.bonus }))
    const nextReinforce = eliminated || hidden ? 0 : reinforcementCount(state, playerId)
    const cardCount = hidden ? 0 : state.cards?.[playerId]?.length ?? 0
    return {
      player: p,
      owned,
      totalTroops,
      eliminated,
      resigned: resignedIds.has(playerId),
      isTurn: playerId === currentTurnPlayerId && !eliminated,
      regionBonuses,
      nextReinforce,
      cardCount,
      ...(hidden ? { fog: { seenTroops: totalTroops, seenTerritories: seen.length, ownershipPublic: fog === 'light' } } : {}),
    }
  }).sort((a, b) => turnPos(a.player.slot) - turnPos(b.player.slot))
}
